import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  PlusCircleIcon,
  SparklesIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import Alert from './Alert';
import PermissionGate from './PermissionGate';
import { useAuth } from '../AuthContext';
import { fetchRewards } from '../services/rewardStoreApi';
import {
  awardManualOrder,
  awardManualReward,
  awardManualTokens,
  createCrmCampaign,
  createCrmUserExport,
  deleteCrmCampaign,
  fetchAppUserCharts,
  fetchAppUserActivity,
  fetchCrmCampaign,
  fetchCrmCampaigns,
  fetchCrmCampaignSegments,
  previewCrmCampaignAudience,
  previewManualOrderAward,
  refreshCrmCampaignReport,
  searchAppUsers,
  sendCrmCampaign,
  testSendCrmCampaign,
} from '../services/userCrmApi';

const pageSizeOptions = [25, 50, 100];
const activityPageSize = 10;
const activityFetchBatchSize = 100;
const referralStatuses = ['awarded', 'pending', 'invalid_code', 'self_referral', 'reversed'];
const manualTokenAwardMax = 1000;
const campaignActions = [
  { value: 'open_app', label: 'Open app' },
  { value: 'open_rewards', label: 'Open rewards' },
  { value: 'open_streak', label: 'Open streak' },
  { value: 'open_referral', label: 'Open referral' },
  { value: 'custom', label: 'Custom deep link' },
];
const sortOptions = [
  { value: 'updatedAt', label: 'Updated' },
  { value: 'createdAt', label: 'Sign Up Date' },
  { value: 'lastVisitDate', label: 'Streak Visit Day' },
  { value: 'tokenBalance', label: 'Token Balance' },
  { value: 'lifetimeNetSpend', label: 'Lifetime Net Spend' },
  { value: 'orderCount', label: 'Orders' },
  { value: 'currentStreak', label: 'Current Streak' },
  { value: 'bestStreak', label: 'Best Streak' },
];
const chartColors = ['#459188', '#2563eb', '#f59e0b', '#dc2626', '#7c3aed', '#0f766e', '#9333ea', '#ea580c'];

function parseDate(value) {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-').map(Number);
    const dateOnly = new Date(year, month - 1, day);
    return Number.isNaN(dateOnly.getTime()) ? null : dateOnly;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(value) {
  const date = parseDate(value);
  if (!date) return '-';
  return date.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatBirthdate(value) {
  const date = parseDate(value);
  if (!date) return '';
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function calculateAge(value) {
  const birthdate = parseDate(value);
  if (!birthdate) return '';

  const today = new Date();
  if (birthdate > today) return '';

  let age = today.getFullYear() - birthdate.getFullYear();
  const birthdayThisYear = new Date(today.getFullYear(), birthdate.getMonth(), birthdate.getDate());
  if (today < birthdayThisYear) age -= 1;

  return age >= 0 ? age : '';
}

function formatNumber(value) {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed.toLocaleString('en-IN') : '0';
}

function formatCurrency(value) {
  const parsed = Number(value || 0);
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(parsed) ? parsed : 0);
}

function dateStart(value) {
  return value ? new Date(`${value}T00:00:00`).toISOString() : undefined;
}

function dateEnd(value) {
  return value ? new Date(`${value}T23:59:59.999`).toISOString() : undefined;
}

function triStateToBool(value) {
  if (value === 'yes') return true;
  if (value === 'no') return false;
  return undefined;
}

function buildFilters(filters) {
  const isRegistered = {
    registered: true,
    guest: false,
    all: undefined,
  }[filters.registrationStatus];

  const payload = {
    createdAtStart: dateStart(filters.createdAtStart),
    createdAtEnd: dateEnd(filters.createdAtEnd),
    lastVisitStart: dateStart(filters.lastVisitStart),
    lastVisitEnd: dateEnd(filters.lastVisitEnd),
    tokenBalanceMin: filters.tokenBalanceMin ? Number(filters.tokenBalanceMin) : undefined,
    tokenBalanceMax: filters.tokenBalanceMax ? Number(filters.tokenBalanceMax) : undefined,
    orderCountMin: filters.orderCountMin ? Number(filters.orderCountMin) : undefined,
    orderCountMax: filters.orderCountMax ? Number(filters.orderCountMax) : undefined,
    currentStreakMin: filters.currentStreakMin ? Number(filters.currentStreakMin) : undefined,
    currentStreakMax: filters.currentStreakMax ? Number(filters.currentStreakMax) : undefined,
    bestStreakMin: filters.bestStreakMin ? Number(filters.bestStreakMin) : undefined,
    bestStreakMax: filters.bestStreakMax ? Number(filters.bestStreakMax) : undefined,
    lifetimeTokensEarnedMin: filters.lifetimeTokensEarnedMin ? Number(filters.lifetimeTokensEarnedMin) : undefined,
    lifetimeTokensEarnedMax: filters.lifetimeTokensEarnedMax ? Number(filters.lifetimeTokensEarnedMax) : undefined,
    lifetimeTokensSpentMin: filters.lifetimeTokensSpentMin ? Number(filters.lifetimeTokensSpentMin) : undefined,
    lifetimeTokensSpentMax: filters.lifetimeTokensSpentMax ? Number(filters.lifetimeTokensSpentMax) : undefined,
    lifetimeNetSpendMin: filters.lifetimeNetSpendMin ? Number(filters.lifetimeNetSpendMin) : undefined,
    lifetimeNetSpendMax: filters.lifetimeNetSpendMax ? Number(filters.lifetimeNetSpendMax) : undefined,
    referralSignupRewardStatus: filters.referralSignupRewardStatus ? [filters.referralSignupRewardStatus] : undefined,
    hasReferralCode: triStateToBool(filters.hasReferralCode),
    hasFcmTokens: triStateToBool(filters.hasFcmTokens),
    isRegistered,
  };

  return Object.entries(payload).reduce((result, [key, value]) => {
    if (value !== undefined && value !== '') result[key] = value;
    return result;
  }, {});
}

function emptyFilters() {
  return {
    createdAtStart: '',
    createdAtEnd: '',
    lastVisitStart: '',
    lastVisitEnd: '',
    tokenBalanceMin: '',
    tokenBalanceMax: '',
    orderCountMin: '',
    orderCountMax: '',
    currentStreakMin: '',
    currentStreakMax: '',
    bestStreakMin: '',
    bestStreakMax: '',
    lifetimeTokensEarnedMin: '',
    lifetimeTokensEarnedMax: '',
    lifetimeTokensSpentMin: '',
    lifetimeTokensSpentMax: '',
    lifetimeNetSpendMin: '',
    lifetimeNetSpendMax: '',
    referralSignupRewardStatus: '',
    hasReferralCode: 'all',
    hasFcmTokens: 'all',
    registrationStatus: 'registered',
  };
}

function countActiveAdvancedFilters(filters) {
  const defaults = emptyFilters();
  return Object.keys(defaults).reduce((count, field) => (
    filters[field] !== defaults[field] && filters[field] !== '' ? count + 1 : count
  ), 0);
}

function dateInputFromIso(value) {
  if (!value) return '';
  return String(value).slice(0, 10);
}

function triStateFromBool(value) {
  if (value === true) return 'yes';
  if (value === false) return 'no';
  return 'all';
}

function filtersFromAudienceFilters(audienceFilters = {}) {
  const next = emptyFilters();
  next.createdAtStart = dateInputFromIso(audienceFilters.createdAtStart);
  next.createdAtEnd = dateInputFromIso(audienceFilters.createdAtEnd);
  next.lastVisitStart = dateInputFromIso(audienceFilters.lastVisitStart);
  next.lastVisitEnd = dateInputFromIso(audienceFilters.lastVisitEnd);
  [
    'tokenBalanceMin',
    'tokenBalanceMax',
    'orderCountMin',
    'orderCountMax',
    'currentStreakMin',
    'currentStreakMax',
    'bestStreakMin',
    'bestStreakMax',
    'lifetimeTokensEarnedMin',
    'lifetimeTokensEarnedMax',
    'lifetimeTokensSpentMin',
    'lifetimeTokensSpentMax',
    'lifetimeNetSpendMin',
    'lifetimeNetSpendMax',
  ].forEach((field) => {
    if (audienceFilters[field] !== undefined && audienceFilters[field] !== null) {
      next[field] = String(audienceFilters[field]);
    }
  });
  if (Array.isArray(audienceFilters.referralSignupRewardStatus) && audienceFilters.referralSignupRewardStatus.length > 0) {
    next.referralSignupRewardStatus = audienceFilters.referralSignupRewardStatus[0];
  }
  next.hasReferralCode = triStateFromBool(audienceFilters.hasReferralCode);
  next.hasFcmTokens = triStateFromBool(audienceFilters.hasFcmTokens);
  if (audienceFilters.isRegistered === true) next.registrationStatus = 'registered';
  if (audienceFilters.isRegistered === false) next.registrationStatus = 'guest';
  if (audienceFilters.isRegistered === undefined) next.registrationStatus = 'all';
  return next;
}

function emptyCampaignForm() {
  return {
    name: '',
    title: '',
    body: '',
    action: 'open_rewards',
    deepLink: '',
  };
}

function percent(value) {
  const parsed = Number(value || 0);
  return `${Math.round(parsed * 100)}%`;
}

function CrmFiltersModal({ filters, onChange, onClose, onApply, onClear }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
      <div className="bg-white rounded shadow-xl w-full max-w-5xl max-h-svh overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between gap-3">
          <div>
            <h3 className="text-xl font-bold">Filters</h3>
            <p className="text-sm text-gray-500 mt-1">Refine the CRM audience without crowding the table controls.</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-800" aria-label="Close filters">
            <XMarkIcon className="size-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <section>
            <h4 className="font-bold mb-3">Dates</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">Sign-up start</span>
                <input type="date" className="w-full border px-3 py-2 rounded" value={filters.createdAtStart} onChange={(event) => onChange('createdAtStart', event.target.value)} />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">Sign-up end</span>
                <input type="date" className="w-full border px-3 py-2 rounded" value={filters.createdAtEnd} onChange={(event) => onChange('createdAtEnd', event.target.value)} />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">Last visit start</span>
                <input type="date" className="w-full border px-3 py-2 rounded" value={filters.lastVisitStart} onChange={(event) => onChange('lastVisitStart', event.target.value)} />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">Last visit end</span>
                <input type="date" className="w-full border px-3 py-2 rounded" value={filters.lastVisitEnd} onChange={(event) => onChange('lastVisitEnd', event.target.value)} />
              </label>
            </div>
          </section>

          <section>
            <h4 className="font-bold mb-3">Loyalty</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">Min tokens</span>
                <input type="number" min="0" className="w-full border px-3 py-2 rounded" value={filters.tokenBalanceMin} onChange={(event) => onChange('tokenBalanceMin', event.target.value)} />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">Max tokens</span>
                <input type="number" min="0" className="w-full border px-3 py-2 rounded" value={filters.tokenBalanceMax} onChange={(event) => onChange('tokenBalanceMax', event.target.value)} />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">Min orders</span>
                <input type="number" min="0" className="w-full border px-3 py-2 rounded" value={filters.orderCountMin} onChange={(event) => onChange('orderCountMin', event.target.value)} />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">Max orders</span>
                <input type="number" min="0" className="w-full border px-3 py-2 rounded" value={filters.orderCountMax} onChange={(event) => onChange('orderCountMax', event.target.value)} />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">Min current streak</span>
                <input type="number" min="0" className="w-full border px-3 py-2 rounded" value={filters.currentStreakMin} onChange={(event) => onChange('currentStreakMin', event.target.value)} />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">Max current streak</span>
                <input type="number" min="0" className="w-full border px-3 py-2 rounded" value={filters.currentStreakMax} onChange={(event) => onChange('currentStreakMax', event.target.value)} />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">Min best streak</span>
                <input type="number" min="0" className="w-full border px-3 py-2 rounded" value={filters.bestStreakMin} onChange={(event) => onChange('bestStreakMin', event.target.value)} />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">Max best streak</span>
                <input type="number" min="0" className="w-full border px-3 py-2 rounded" value={filters.bestStreakMax} onChange={(event) => onChange('bestStreakMax', event.target.value)} />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">Min earned</span>
                <input type="number" min="0" className="w-full border px-3 py-2 rounded" value={filters.lifetimeTokensEarnedMin} onChange={(event) => onChange('lifetimeTokensEarnedMin', event.target.value)} />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">Max earned</span>
                <input type="number" min="0" className="w-full border px-3 py-2 rounded" value={filters.lifetimeTokensEarnedMax} onChange={(event) => onChange('lifetimeTokensEarnedMax', event.target.value)} />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">Min spent</span>
                <input type="number" min="0" className="w-full border px-3 py-2 rounded" value={filters.lifetimeTokensSpentMin} onChange={(event) => onChange('lifetimeTokensSpentMin', event.target.value)} />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">Max spent</span>
                <input type="number" min="0" className="w-full border px-3 py-2 rounded" value={filters.lifetimeTokensSpentMax} onChange={(event) => onChange('lifetimeTokensSpentMax', event.target.value)} />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">Min net spend (₹)</span>
                <input type="number" min="0" step="0.01" className="w-full border px-3 py-2 rounded" value={filters.lifetimeNetSpendMin} onChange={(event) => onChange('lifetimeNetSpendMin', event.target.value)} />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-600 mb-1">Max net spend (₹)</span>
                <input type="number" min="0" step="0.01" className="w-full border px-3 py-2 rounded" value={filters.lifetimeNetSpendMax} onChange={(event) => onChange('lifetimeNetSpendMax', event.target.value)} />
              </label>
            </div>
          </section>

          <section>
            <h4 className="font-bold mb-3">Audience</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
              <select className="border px-3 py-2 rounded" value={filters.referralSignupRewardStatus} onChange={(event) => onChange('referralSignupRewardStatus', event.target.value)}>
                <option value="">All referral statuses</option>
                {referralStatuses.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
              <select className="border px-3 py-2 rounded" value={filters.hasReferralCode} onChange={(event) => onChange('hasReferralCode', event.target.value)}>
                <option value="all">Referral code: all</option>
                <option value="yes">Has referral code</option>
                <option value="no">No referral code</option>
              </select>
              <select className="border px-3 py-2 rounded" value={filters.hasFcmTokens} onChange={(event) => onChange('hasFcmTokens', event.target.value)}>
                <option value="all">FCM tokens: all</option>
                <option value="yes">Has FCM tokens</option>
                <option value="no">No FCM tokens</option>
              </select>
              <select className="border px-3 py-2 rounded" value={filters.registrationStatus} onChange={(event) => onChange('registrationStatus', event.target.value)}>
                <option value="registered">Registered Users</option>
                <option value="guest">Guest Users</option>
                <option value="all">All Users</option>
              </select>
            </div>
          </section>
        </div>

        <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex flex-col md:flex-row md:justify-end gap-2">
          <button type="button" onClick={onClear} className="border px-4 py-2 rounded hover:bg-gray-50">
            Clear Filters
          </button>
          <button type="button" onClick={onClose} className="border px-4 py-2 rounded hover:bg-gray-50">
            Cancel
          </button>
          <button type="button" onClick={onApply} className="bg-teal-600 text-white px-4 py-2 rounded flex items-center justify-center gap-2">
            <FunnelIcon className="size-5" />
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  const displayValue = value === null || value === undefined || value === '' ? '-' : value;

  return (
    <div>
      <div className="text-xs uppercase text-gray-500 font-bold">{label}</div>
      <div className="mt-1 text-sm text-gray-900 break-words">{displayValue}</div>
    </div>
  );
}

function GuestBadge({ user }) {
  if (user?.isRegistered !== false) return null;

  return (
    <span className="inline-flex items-center rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-semibold uppercase text-amber-700">
      Guest
    </span>
  );
}

function AppUserBadge({ user }) {
  if (user?.isRegistered !== true) return null;

  return (
    <span className="inline-flex items-center rounded-full border border-teal-300 bg-teal-50 px-2 py-0.5 text-xs font-semibold uppercase text-teal-700">
      App User
    </span>
  );
}

function normalizeManualOrderInput(value) {
  return String(value || '').replace(/\//g, '_');
}

function ManualOrderAwardModal({ user, onClose, onAwardComplete }) {
  const [orderId, setOrderId] = useState('');
  const [preview, setPreview] = useState(null);
  const [awardResult, setAwardResult] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [awarding, setAwarding] = useState(false);
  const [error, setError] = useState('');

  const resetPreview = (value) => {
    setOrderId(normalizeManualOrderInput(value));
    setPreview(null);
    setAwardResult(null);
    setError('');
  };

  const handlePreview = async (event) => {
    event.preventDefault();
    if (!orderId.trim()) {
      setError('Order ID is required.');
      return;
    }

    setLoadingPreview(true);
    setError('');
    setAwardResult(null);
    try {
      const result = await previewManualOrderAward({ userId: user.id, orderId: orderId.trim() });
      setPreview(result);
    } catch (err) {
      setError(err.message || 'Failed to check order.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleAward = async () => {
    setAwarding(true);
    setError('');
    try {
      const result = await awardManualOrder({ userId: user.id, orderId: orderId.trim() });
      setAwardResult(result);
      if (result.awarded) {
        onAwardComplete(result);
      }
    } catch (err) {
      setError(err.message || 'Failed to award order.');
    } finally {
      setAwarding(false);
    }
  };

  const resolvedOrder = preview?.resolvedOrder || awardResult?.preview?.resolvedOrder;
  const tokenAward = preview?.tokenAward || awardResult?.preview?.tokenAward;
  const isEligible = preview?.eligible === true && !awardResult;
  const awarded = awardResult?.awarded === true;
  const isRepair = (preview?.mode || awardResult?.preview?.mode) === 'repair';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
      <div className="bg-white rounded shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="border-b px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold">{isRepair ? 'Repair Order Award' : 'Award Order'}</h3>
            <p className="text-sm text-gray-500">{user.fullName || user.displayName || user.phoneNumber || user.id}</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-800" aria-label="Close award order">
            <XMarkIcon className="size-6" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <form onSubmit={handlePreview} className="flex flex-col md:flex-row gap-3">
            <input
              type="text"
              className="border px-3 py-2 rounded flex-1"
              value={orderId}
              onChange={(event) => resetPreview(event.target.value)}
              placeholder="Rista order ID or order number"
              disabled={loadingPreview || awarding || awarded}
            />
            <button
              type="submit"
              className="bg-teal-500 text-white px-4 py-2 rounded flex items-center justify-center gap-2 disabled:opacity-60"
              disabled={loadingPreview || awarding || awarded}
            >
              {loadingPreview && <ArrowPathIcon className="size-5 animate-spin" />}
              Check
            </button>
          </form>

          {error && <div className="border border-red-200 bg-red-50 text-red-700 rounded px-3 py-2 text-sm">{error}</div>}

          {preview && (
            <div className={`border rounded p-4 ${preview.eligible ? 'border-teal-200 bg-teal-50' : 'border-amber-200 bg-amber-50'}`}>
              <div className="font-semibold">
                {preview.eligible ? (preview.mode === 'repair' ? 'Ready to repair' : 'Ready to award') : 'Cannot award this order'}
              </div>
              {!preview.eligible && <div className="text-sm mt-1 text-amber-800">{preview.reason || 'Order is not eligible.'}</div>}

              {resolvedOrder && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                  <DetailRow label="Order ID" value={resolvedOrder.orderId} />
                  <DetailRow label="Order No" value={resolvedOrder.orderNo} />
                  <DetailRow label="Channel" value={resolvedOrder.channel} />
                  <DetailRow label="Status" value={resolvedOrder.status} />
                  <DetailRow label="Order Time" value={formatDate(resolvedOrder.orderTime)} />
                  <DetailRow label="Customer Phone" value={resolvedOrder.customerPhone} />
                </div>
              )}

              {tokenAward && (
                <div className="mt-4 border rounded bg-white px-3 py-3">
                  {preview.mode === 'repair' ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <DetailRow label="Calculated Tokens" value={formatNumber(preview.authoritativeOrderTokens)} />
                      <DetailRow label="Already Credited" value={formatNumber(preview.postedLedgerTokens)} />
                      <DetailRow label="Balance Change" value={`+${formatNumber(preview.balanceDelta)}`} />
                    </div>
                  ) : (
                    <>
                      <div className="text-xs text-gray-500">Tokens to award</div>
                      <div className="text-2xl font-bold text-teal-600">{formatNumber(tokenAward.totalTokens)}</div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {awardResult && (
            <div className={`border rounded p-4 ${awardResult.awarded ? 'border-teal-200 bg-teal-50' : 'border-amber-200 bg-amber-50'}`}>
              <div className="font-semibold">
                {awardResult.awarded ? (awardResult.repaired ? 'Order award repaired' : 'Order awarded') : 'Order not awarded'}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                <DetailRow label="Tokens" value={formatNumber(awardResult.loyaltyResult?.tokensAwarded)} />
                <DetailRow label="Order Tokens" value={formatNumber(awardResult.loyaltyResult?.orderTokensAwarded)} />
                <DetailRow
                  label={awardResult.repaired ? 'Balance Change' : 'Referral Bonus'}
                  value={formatNumber(awardResult.repaired ? awardResult.loyaltyResult?.balanceDelta : awardResult.loyaltyResult?.referralFirstOrderTokensAwarded)}
                />
              </div>
            </div>
          )}

          <div className="flex flex-col md:flex-row md:justify-end gap-2">
            <button type="button" className="border px-4 py-2 rounded hover:bg-gray-50" onClick={onClose}>
              {awarded ? 'Done' : 'Cancel'}
            </button>
            <button
              type="button"
              className="bg-teal-600 text-white px-4 py-2 rounded flex items-center justify-center gap-2 disabled:opacity-60"
              onClick={handleAward}
              disabled={!isEligible || awarding}
            >
              {awarding && <ArrowPathIcon className="size-5 animate-spin" />}
              {isRepair ? 'Repair Award' : 'Award Tokens'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ManualTokenAwardModal({ user, onClose, onAwardComplete }) {
  const [amount, setAmount] = useState(0);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const parsedAmount = Number(amount);
  const sliderAmount = Number.isFinite(parsedAmount)
    ? Math.round(Math.max(0, Math.min(manualTokenAwardMax, parsedAmount)))
    : 0;
  const sliderPercent = manualTokenAwardMax > 0
    ? Math.round((sliderAmount / manualTokenAwardMax) * 100)
    : 0;

  const handleAmountChange = (value) => {
    setAmount(value);
    if (error) setError('');
  };

  const handleSliderChange = (event) => {
    handleAmountChange(Number(event.target.value));
  };

  const handleNumberInputChange = (event) => {
    handleAmountChange(event.target.value);
  };

  const handleReasonChange = (event) => {
    setReason(event.target.value);
    if (error) setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedReason = reason.trim();
    const amountValue = String(amount).trim();
    const tokenAmount = Number(amountValue);
    if (!amountValue) {
      setError('Token amount is required.');
      return;
    }
    if (!Number.isInteger(tokenAmount) || tokenAmount <= 0) {
      setError('Token amount must be a whole number greater than 0.');
      return;
    }
    if (tokenAmount > manualTokenAwardMax) {
      setError(`Token amount cannot exceed ${formatNumber(manualTokenAwardMax)}.`);
      return;
    }
    if (!trimmedReason) {
      setError('Reason is required.');
      return;
    }
    if (trimmedReason.length > 280) {
      setError('Reason must be 280 characters or fewer.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const response = await awardManualTokens({ userId: user.id, amount: tokenAmount, reason: trimmedReason });
      setResult(response);
      if (response && response.awarded) {
        onAwardComplete(response);
      }
    } catch (err) {
      setError(err.message || 'Failed to award tokens.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const posted = result?.awarded === true;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
      <div className="bg-white rounded shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="border-b px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold">Award Tokens</h3>
            <p className="text-sm text-gray-500">{user.fullName || user.displayName || user.phoneNumber || user.id}</p>
          </div>
          <button type="button" onClick={handleClose} className="text-gray-500 hover:text-gray-800" aria-label="Close award tokens">
            <XMarkIcon className="size-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="manual-token-amount" className="font-semibold text-sm">Tokens to award</label>
              <div className="text-2xl font-bold text-teal-600">{formatNumber(amount)}</div>
            </div>
            <input
              id="manual-token-amount"
              type="range"
              min="0"
              max={manualTokenAwardMax}
              step="1"
              value={sliderAmount}
              onChange={handleSliderChange}
              disabled={submitting || posted}
              className="w-full accent-teal-500"
              style={{ background: `linear-gradient(to right, #459188 0%, #459188 ${sliderPercent}%, #e5e7eb ${sliderPercent}%, #e5e7eb 100%)` }}
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>0</span>
              <span>{formatNumber(manualTokenAwardMax)} max</span>
            </div>
            <label htmlFor="manual-token-amount-input" className="block mt-4 font-semibold text-sm">
              Exact token amount
            </label>
            <input
              id="manual-token-amount-input"
              type="number"
              min="1"
              max={manualTokenAwardMax}
              step="1"
              inputMode="numeric"
              value={amount}
              onChange={handleNumberInputChange}
              disabled={submitting || posted}
              className="mt-2 w-full border px-3 py-2 rounded text-sm"
              aria-describedby="manual-token-amount-help"
            />
            <p id="manual-token-amount-help" className="text-xs text-gray-500 mt-1">
              Enter any whole number from 1 to {formatNumber(manualTokenAwardMax)}.
            </p>
          </div>

          <div>
            <label htmlFor="manual-token-reason" className="font-semibold text-sm block mb-2">Reason</label>
            <textarea
              id="manual-token-reason"
              className="w-full border px-3 py-2 rounded text-sm"
              rows={3}
              value={reason}
              onChange={handleReasonChange}
              placeholder="e.g. Apology for delayed order at GK2"
              maxLength={280}
              disabled={submitting || posted}
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Shown to the user in their push notification and token ledger.</span>
              <span>{reason.length}/280</span>
            </div>
          </div>

          {error && <div className="border border-red-200 bg-red-50 text-red-700 rounded px-3 py-2 text-sm">{error}</div>}

          {result && (
            <div className={`border rounded p-4 ${posted ? 'border-teal-200 bg-teal-50' : 'border-amber-200 bg-amber-50'}`}>
              <div className="font-semibold">{posted ? 'Tokens awarded' : 'Tokens not awarded'}</div>
              {posted && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                  <DetailRow label="Awarded" value={formatNumber(result.amount)} />
                  <DetailRow label="Balance Before" value={formatNumber(result.tokenBalanceBefore)} />
                  <DetailRow label="Balance After" value={formatNumber(result.tokenBalanceAfter)} />
                </div>
              )}
              {result.notification && (
                <div className="mt-3 text-xs text-gray-600">
                  Push notification: {result.notification.success
                    ? `sent (${result.notification.successCount} device${result.notification.successCount === 1 ? '' : 's'})`
                    : (result.notification.error || 'not sent')}
                </div>
              )}
            </div>
          )}

          <div className="flex flex-col md:flex-row md:justify-end gap-2">
            <button type="button" className="border px-4 py-2 rounded hover:bg-gray-50 disabled:opacity-60" onClick={handleClose} disabled={submitting}>
              {posted ? 'Done' : 'Cancel'}
            </button>
            <button
              type="submit"
              className="bg-teal-600 text-white px-4 py-2 rounded flex items-center justify-center gap-2 disabled:opacity-60"
              disabled={submitting || posted || !reason.trim()}
            >
              {submitting && <ArrowPathIcon className="size-5 animate-spin" />}
              Award Tokens
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function createRewardAwardId() {
  if (window.crypto?.randomUUID) return `admin_reward_${window.crypto.randomUUID()}`;
  return `admin_reward_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function ManualRewardAwardModal({ user, onClose, onAwardComplete }) {
  const [rewards, setRewards] = useState([]);
  const [rewardId, setRewardId] = useState('');
  const [reason, setReason] = useState('');
  const [loadingRewards, setLoadingRewards] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [awardId] = useState(createRewardAwardId);

  useEffect(() => {
    let active = true;
    fetchRewards()
      .then((items) => {
        if (!active) return;
        setRewards(items || []);
      })
      .catch((err) => {
        if (active) setError(err.message || 'Failed to load Reward Store items.');
      })
      .finally(() => {
        if (active) setLoadingRewards(false);
      });
    return () => { active = false; };
  }, []);

  const selectedReward = rewards.find((reward) => reward.id === rewardId) || null;
  const posted = result?.awarded === true;

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedReason = reason.trim();
    if (!selectedReward) {
      setError('Select a Reward Store item.');
      return;
    }
    if (!trimmedReason) {
      setError('Reason is required.');
      return;
    }
    if (trimmedReason.length > 280) {
      setError('Reason must be 280 characters or fewer.');
      return;
    }
    if (!window.confirm(`Grant “${selectedReward.title}” to ${user.fullName || user.displayName || user.phoneNumber || user.id}? No Got Tokens will be deducted.`)) {
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const response = await awardManualReward({
        userId: user.id,
        rewardId: selectedReward.id,
        reason: trimmedReason,
        awardId,
      });
      setResult(response);
      if (response?.awarded) onAwardComplete(response);
    } catch (err) {
      setError(err.message || 'Failed to grant reward.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
      <div className="bg-white rounded shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="border-b px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold">Grant Reward</h3>
            <p className="text-sm text-gray-500">{user.fullName || user.displayName || user.phoneNumber || user.id}</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-800" aria-label="Close grant reward">
            <XMarkIcon className="size-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <label htmlFor="manual-reward-item" className="font-semibold text-sm block mb-2">Reward Store item</label>
            <select
              id="manual-reward-item"
              className="w-full border px-3 py-2 rounded text-sm"
              value={rewardId}
              onChange={(event) => { setRewardId(event.target.value); setError(''); }}
              disabled={loadingRewards || submitting || posted}
            >
              <option value="">{loadingRewards ? 'Loading rewards…' : 'Select a reward'}</option>
              {rewards.map((reward) => (
                <option key={reward.id} value={reward.id}>
                  {reward.title} — {reward.status} — {formatNumber(reward.tokenCost)} tokens
                </option>
              ))}
            </select>
            {selectedReward && (
              <p className="text-xs text-gray-500 mt-2">
                {selectedReward.status === 'inactive' ? 'Inactive catalog reward; it will still be granted.' : 'Active catalog reward.'}
                {' '}The customer receives it free using its configured validity and Rista discount.
              </p>
            )}
          </div>

          <div>
            <label htmlFor="manual-reward-reason" className="font-semibold text-sm block mb-2">Reason</label>
            <textarea
              id="manual-reward-reason"
              className="w-full border px-3 py-2 rounded text-sm"
              rows={3}
              value={reason}
              onChange={(event) => { setReason(event.target.value); setError(''); }}
              placeholder="e.g. Apology for delayed order at GK2"
              maxLength={280}
              disabled={submitting || posted}
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>Recorded in the award audit trail and included in the push notification.</span>
              <span>{reason.length}/280</span>
            </div>
          </div>

          {error && <div className="border border-red-200 bg-red-50 text-red-700 rounded px-3 py-2 text-sm">{error}</div>}

          {result && (
            <div className="border border-teal-200 bg-teal-50 rounded p-4">
              <div className="font-semibold">{result.alreadyAwarded ? 'Reward was already granted' : 'Reward granted'}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                <DetailRow label="Reward" value={result.reward?.title || selectedReward?.title} />
                <DetailRow label="Coupon code" value={result.couponCode} />
              </div>
              {result.notification && (
                <div className="mt-3 text-xs text-gray-600">
                  Push notification: {result.notification.success
                    ? `sent (${result.notification.successCount} device${result.notification.successCount === 1 ? '' : 's'})`
                    : (result.notification.error || 'not sent')}
                </div>
              )}
            </div>
          )}

          <div className="flex flex-col md:flex-row md:justify-end gap-2">
            <button type="button" className="border px-4 py-2 rounded hover:bg-gray-50 disabled:opacity-60" onClick={onClose} disabled={submitting}>
              {posted ? 'Done' : 'Cancel'}
            </button>
            <button
              type="submit"
              className="bg-teal-600 text-white px-4 py-2 rounded flex items-center justify-center gap-2 disabled:opacity-60"
              disabled={loadingRewards || submitting || posted || !rewardId || !reason.trim()}
            >
              {submitting && <ArrowPathIcon className="size-5 animate-spin" />}
              Grant Reward
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function chartTotal(rows) {
  return rows.reduce((sum, row) => sum + Number(row.count || 0), 0);
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;
  const item = payload[0];
  const name = item.payload?.label || label || item.name;

  return (
    <div className="bg-white border rounded shadow px-3 py-2 text-sm">
      <div className="font-semibold">{name}</div>
      <div className="text-gray-600">{formatNumber(item.value)} users</div>
    </div>
  );
}

function ChartCard({ title, summary, loading, children }) {
  return (
    <section className="bg-white rounded shadow p-4 min-h-[340px]">
      <div className="mb-4">
        <h3 className="text-lg font-bold">{title}</h3>
        <p className="text-sm text-gray-500 mt-1">{loading ? 'Loading...' : summary}</p>
      </div>
      {loading ? (
        <div className="h-64 flex items-center justify-center">
          <ArrowPathIcon className="size-8 text-teal-500 animate-spin" />
        </div>
      ) : (
        children
      )}
    </section>
  );
}

function EmptyChart() {
  return <div className="h-64 flex items-center justify-center text-sm text-gray-500">No matching data.</div>;
}

function PieMetricChart({ rows }) {
  const filteredRows = rows.filter((row) => Number(row.count || 0) > 0);
  const total = chartTotal(filteredRows);

  if (total <= 0) return <EmptyChart />;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-4 items-center">
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={filteredRows} dataKey="count" nameKey="label" innerRadius={52} outerRadius={86} paddingAngle={2}>
              {filteredRows.map((row, index) => (
                <Cell key={row.key} fill={chartColors[index % chartColors.length]} />
              ))}
            </Pie>
            <Tooltip content={<ChartTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div className="space-y-2">
        {filteredRows.map((row, index) => {
          const percent = total > 0 ? Math.round((Number(row.count || 0) / total) * 100) : 0;
          return (
            <div key={row.key} className="flex items-center justify-between gap-3 text-sm">
              <div className="flex items-center gap-2 min-w-0">
                <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: chartColors[index % chartColors.length] }} />
                <span className="font-medium truncate">{row.label}</span>
              </div>
              <span className="text-gray-600 shrink-0">{formatNumber(row.count)} ({percent}%)</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BarMetricChart({ rows, compact = false, showAllTicks = true }) {
  const total = chartTotal(rows);

  if (total <= 0) return <EmptyChart />;

  return (
    <div className={compact ? 'h-56' : 'h-64'}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} margin={{ top: 8, right: 12, left: 8, bottom: compact ? 28 : 20 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
          <XAxis
            dataKey="label"
            interval={showAllTicks ? 0 : 'preserveStartEnd'}
            angle={compact ? -25 : 0}
            textAnchor={compact ? 'end' : 'middle'}
            height={compact ? 54 : 36}
            tick={{ fontSize: 12, fill: '#4b5563' }}
          />
          <YAxis width={44} tickFormatter={formatNumber} tick={{ fontSize: 12, fill: '#4b5563' }} />
          <Tooltip content={<ChartTooltip />} />
          <Bar dataKey="count" radius={[4, 4, 0, 0]} fill="#459188" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function ChartsDashboard({ charts, loading, signupTrendDateRange, onSignupTrendDateChange, onResetSignupTrendDate }) {
  const ageRows = charts?.ageGroups?.groups || [];
  const knownAgeTotal = chartTotal(ageRows);
  const missingDobCount = Number(charts?.ageGroups?.missingDateOfBirthCount || 0);
  const totalCount = Number(charts?.totalCount || 0);
  const dailyTrendRows = charts?.signupTrendDaily || [];
  const weeklyTrendRows = charts?.signupTrendWeekly || [];
  const trendSummary = signupTrendDateRange.start || signupTrendDateRange.end
    ? `${signupTrendDateRange.start || '…'} → ${signupTrendDateRange.end || '…'}`
    : 'All time';

  return (
    <div className="space-y-5">
      <div className="bg-white rounded shadow p-4 flex flex-col md:flex-row md:items-end gap-3">
        <div className="flex-1">
          <h3 className="text-lg font-bold">Signup Trend Date Filter</h3>
          <p className="text-sm text-gray-500 mt-1">Restrict the monthly / weekly / daily signup charts to a specific window.</p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs uppercase text-gray-500 font-bold mb-1">From</label>
            <input
              type="date"
              className="border px-3 py-2 rounded"
              value={signupTrendDateRange.start}
              onChange={(event) => onSignupTrendDateChange('start', event.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs uppercase text-gray-500 font-bold mb-1">To</label>
            <input
              type="date"
              className="border px-3 py-2 rounded"
              value={signupTrendDateRange.end}
              onChange={(event) => onSignupTrendDateChange('end', event.target.value)}
            />
          </div>
          <button
            type="button"
            onClick={onResetSignupTrendDate}
            className="border px-4 py-2 rounded hover:bg-gray-50"
          >
            Clear
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <ChartCard
          title="Age Groups"
          summary={`${formatNumber(knownAgeTotal)} users with birthdates, ${formatNumber(missingDobCount)} missing`}
          loading={loading}
        >
          <PieMetricChart rows={ageRows} />
          <div className="border-t mt-4 pt-3 text-sm text-gray-500 flex justify-between gap-3">
            <span>Missing birthdate</span>
            <span>{formatNumber(missingDobCount)} of {formatNumber(totalCount)}</span>
          </div>
        </ChartCard>

        <ChartCard title="Monthly Signups" summary={`${trendSummary} • ${formatNumber(chartTotal(charts?.signupTrend || []))} users`} loading={loading}>
          <BarMetricChart rows={charts?.signupTrend || []} showAllTicks={false} />
        </ChartCard>

        <ChartCard title="Weekly Signups" summary={`${trendSummary} • ${formatNumber(chartTotal(weeklyTrendRows))} users`} loading={loading}>
          <BarMetricChart rows={weeklyTrendRows} showAllTicks={false} />
        </ChartCard>

        <ChartCard title="Daily Signups" summary={`${trendSummary} • ${formatNumber(chartTotal(dailyTrendRows))} users`} loading={loading}>
          <BarMetricChart rows={dailyTrendRows} showAllTicks={false} />
        </ChartCard>

        <ChartCard title="Order Frequency" summary={`${formatNumber(chartTotal(charts?.orderCountBands || []))} users`} loading={loading}>
          <BarMetricChart rows={charts?.orderCountBands || []} />
        </ChartCard>

        <ChartCard title="Token Balance Distribution" summary={`${formatNumber(chartTotal(charts?.tokenBalanceBands || []))} users`} loading={loading}>
          <BarMetricChart rows={charts?.tokenBalanceBands || []} />
        </ChartCard>

        <ChartCard title="Current Streak Distribution" summary={`${formatNumber(chartTotal(charts?.currentStreakBands || []))} users`} loading={loading}>
          <BarMetricChart rows={charts?.currentStreakBands || []} />
        </ChartCard>

        <ChartCard title="Referral Signup Status" summary={`${formatNumber(chartTotal(charts?.referralSignupStatuses || []))} users`} loading={loading}>
          <BarMetricChart rows={charts?.referralSignupStatuses || []} compact />
        </ChartCard>

        <ChartCard title="First Order Referral Status" summary={`${formatNumber(chartTotal(charts?.referralFirstOrderStatuses || []))} users`} loading={loading}>
          <BarMetricChart rows={charts?.referralFirstOrderStatuses || []} compact />
        </ChartCard>
      </div>
    </div>
  );
}

function CampaignsDashboard({ currentAudience, onApplyAudience, onExportAudience, setAlertMessage }) {
  const [segments, setSegments] = useState([]);
  const [segmentCounts, setSegmentCounts] = useState({});
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingSegments, setLoadingSegments] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [testSearchLoading, setTestSearchLoading] = useState(false);
  const [testSearchQuery, setTestSearchQuery] = useState('');
  const [testSearchResults, setTestSearchResults] = useState([]);
  const [selectedTestUser, setSelectedTestUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [campaignForm, setCampaignForm] = useState(emptyCampaignForm);

  const loadCampaigns = async () => {
    setLoadingCampaigns(true);
    try {
      const result = await fetchCrmCampaigns();
      setCampaigns(result.campaigns || []);
    } catch (error) {
      setAlertMessage(error.message || 'Failed to load CRM campaigns.');
    } finally {
      setLoadingCampaigns(false);
    }
  };

  const loadCampaignDetail = async (campaignId) => {
    try {
      const result = await fetchCrmCampaign(campaignId);
      setSelectedCampaign(result);
    } catch (error) {
      setAlertMessage(error.message || 'Failed to load CRM campaign.');
    }
  };

  useEffect(() => {
    let active = true;
    const loadSegments = async () => {
      setLoadingSegments(true);
      try {
        const result = await fetchCrmCampaignSegments();
        const nextSegments = result.segments || [];
        if (!active) return;
        setSegments(nextSegments);
        const counts = {};
        await Promise.all(nextSegments.map(async (segment) => {
          try {
            const segmentPreview = await previewCrmCampaignAudience({ presetId: segment.id, sampleSize: 1 });
            counts[segment.id] = segmentPreview.totalCount || 0;
          } catch (error) {
            counts[segment.id] = null;
          }
        }));
        if (active) setSegmentCounts(counts);
      } catch (error) {
        setAlertMessage(error.message || 'Failed to load CRM campaign segments.');
      } finally {
        if (active) setLoadingSegments(false);
      }
    };
    loadSegments();
    loadCampaigns();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previewAudience = async (audience = currentAudience) => {
    setLoadingPreview(true);
    try {
      const result = await previewCrmCampaignAudience({ audience, sampleSize: 10 });
      setPreview(result);
    } catch (error) {
      setAlertMessage(error.message || 'Failed to preview CRM campaign audience.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const applySegment = async (segment) => {
    onApplyAudience(segment.audience);
    await previewAudience(segment.audience);
  };

  const handleFormChange = (field, value) => {
    setCampaignForm((previous) => ({ ...previous, [field]: value }));
  };

  const searchTestUsers = async () => {
    const trimmed = testSearchQuery.trim();
    if (!trimmed) {
      setAlertMessage('Enter a phone number, name, email, or user ID to search.');
      return;
    }
    setTestSearchLoading(true);
    setSelectedTestUser(null);
    try {
      const result = await searchAppUsers({
        query: trimmed,
        filters: { isRegistered: true },
        sort: { field: 'updatedAt', direction: 'desc' },
        page: 1,
        pageSize: 8,
      });
      const nextUsers = result.users || [];
      setTestSearchResults(nextUsers);
      if (nextUsers.length === 0) {
        setAlertMessage('No matching users found for test push.');
      }
    } catch (error) {
      setAlertMessage(error.message || 'Failed to search test push users.');
      setTestSearchResults([]);
    } finally {
      setTestSearchLoading(false);
    }
  };

  const chooseTestUser = (user) => {
    setSelectedTestUser(user);
    setTestSearchQuery(user.phoneNumber || user.email || user.fullName || user.id);
    setTestSearchResults([]);
  };

  const buildCampaignPayload = () => ({
    name: campaignForm.name,
    audience: currentAudience,
    message: {
      title: campaignForm.title,
      body: campaignForm.body,
    },
    action: campaignForm.action,
    deepLink: campaignForm.action === 'custom' ? campaignForm.deepLink : '',
  });

  const createDraft = async () => {
    setSubmitting(true);
    try {
      const result = await createCrmCampaign(buildCampaignPayload());
      setAlertMessage('Campaign draft created.');
      setCampaignForm((previous) => ({ ...previous, name: '', title: '', body: '', deepLink: '' }));
      await loadCampaigns();
      await loadCampaignDetail(result.campaign.id);
    } catch (error) {
      setAlertMessage(error.message || 'Failed to create CRM campaign draft.');
    } finally {
      setSubmitting(false);
    }
  };

  const activeCampaignId = selectedCampaign?.campaign?.id;

  const handleTestSend = async () => {
    if (!activeCampaignId) {
      setAlertMessage('Create or select a draft before sending a test.');
      return;
    }
    if (!selectedTestUser?.id) {
      setAlertMessage('Search and select a user for the test send.');
      return;
    }
    setTestSearchResults([]);
    setSubmitting(true);
    try {
      const result = await testSendCrmCampaign(activeCampaignId, selectedTestUser.id);
      setAlertMessage(result.sent ? 'Test push sent.' : `Test push skipped: ${result.reason || 'not sent'}.`);
    } catch (error) {
      setAlertMessage(error.message || 'Failed to send test push.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendCampaign = async () => {
    if (!activeCampaignId) return;
    const campaignName = selectedCampaign?.campaign?.name || activeCampaignId;
    const ok = window.confirm(`Send campaign "${campaignName}" to its current audience?`);
    if (!ok) return;
    setSubmitting(true);
    try {
      await sendCrmCampaign(activeCampaignId);
      setAlertMessage('Campaign sent.');
      await loadCampaigns();
      await loadCampaignDetail(activeCampaignId);
    } catch (error) {
      setAlertMessage(error.message || 'Failed to send CRM campaign.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRefreshReport = async () => {
    if (!activeCampaignId) return;
    setSubmitting(true);
    try {
      await refreshCrmCampaignReport(activeCampaignId);
      setAlertMessage('Campaign report refreshed.');
      await loadCampaignDetail(activeCampaignId);
      await loadCampaigns();
    } catch (error) {
      setAlertMessage(error.message || 'Failed to refresh CRM campaign report.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteDraft = async () => {
    if (!activeCampaignId || selectedCampaign?.campaign?.status !== 'draft') return;
    const campaignName = selectedCampaign?.campaign?.name || activeCampaignId;
    const ok = window.confirm(`Delete draft campaign "${campaignName}"? This cannot be undone.`);
    if (!ok) return;
    setSubmitting(true);
    try {
      await deleteCrmCampaign(activeCampaignId);
      setSelectedCampaign(null);
      setAlertMessage('Draft campaign deleted.');
      await loadCampaigns();
    } catch (error) {
      setAlertMessage(error.message || 'Failed to delete CRM campaign draft.');
    } finally {
      setSubmitting(false);
    }
  };

  const selected = selectedCampaign?.campaign;
  const stats = selected?.stats || {};
  const report = selected?.report || {};
  const canSend = selected && (selected.status === 'draft' || selected.status === 'failed');

  return (
    <div className="space-y-5">
      <section className="bg-white rounded shadow p-4">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-bold">Repeat Visit Segments</h3>
            <p className="text-sm text-gray-500 mt-1">Apply a preset, adjust filters above, then preview or create a push campaign.</p>
          </div>
          {loadingSegments && <ArrowPathIcon className="size-5 text-teal-500 animate-spin" />}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {segments.map((segment) => (
            <button
              type="button"
              key={segment.id}
              onClick={() => applySegment(segment)}
              className="text-left border rounded p-4 hover:border-teal-500 hover:bg-teal-50 transition-colors"
              disabled={loadingPreview}
            >
              <div className="font-bold">{segment.label}</div>
              <div className="text-2xl font-bold text-teal-600 mt-2">
                {segmentCounts[segment.id] === null || segmentCounts[segment.id] === undefined ? '-' : formatNumber(segmentCounts[segment.id])}
              </div>
              <div className="text-xs text-gray-500 mt-2">{segment.description}</div>
            </button>
          ))}
        </div>
      </section>

      <section className="bg-white rounded shadow p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-bold">Audience Preview</h3>
            <p className="text-sm text-gray-500 mt-1">Uses the CRM filters currently shown above.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => previewAudience()} className="border px-4 py-2 rounded flex items-center gap-2 hover:bg-gray-50" disabled={loadingPreview}>
              <MagnifyingGlassIcon className="size-5" />
              {loadingPreview ? 'Previewing...' : 'Preview Audience'}
            </button>
            <PermissionGate permission="crm.users.export"><button type="button" onClick={onExportAudience} className="border px-4 py-2 rounded flex items-center gap-2 hover:bg-gray-50">
              <ArrowDownTrayIcon className="size-5" />
              Export Current Audience
            </button></PermissionGate>
          </div>
        </div>
        {preview ? (
          <div className="space-y-3">
            <div className="text-sm text-gray-600">{formatNumber(preview.totalCount)} matching users. Showing up to 10 sample users.</div>
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead>
                  <tr>
                    <th className="py-2 px-3 border-b">User</th>
                    <th className="py-2 px-3 border-b">Contact</th>
                    <th className="py-2 px-3 border-b">Orders</th>
                    <th className="py-2 px-3 border-b">Tokens</th>
                    <th className="py-2 px-3 border-b">Last Visit</th>
                  </tr>
                </thead>
                <tbody>
                  {(preview.sampleUsers || []).map((user) => (
                    <tr key={user.id}>
                      <td className="py-2 px-3 border-b">
                        <div className="font-semibold">{user.fullName || user.displayName || user.name || user.id}</div>
                        <div className="text-xs text-gray-500">{user.id}</div>
                      </td>
                      <td className="py-2 px-3 border-b">
                        <div>{user.phoneNumber || '-'}</div>
                        <div className="text-xs text-gray-500">{user.email || '-'}</div>
                      </td>
                      <td className="py-2 px-3 border-b">{formatNumber(user.orderCount)}</td>
                      <td className="py-2 px-3 border-b">{formatNumber(user.tokenBalance)}</td>
                      <td className="py-2 px-3 border-b">{formatDate(user.lastVisitDate)}</td>
                    </tr>
                  ))}
                  {(!preview.sampleUsers || preview.sampleUsers.length === 0) && (
                    <tr><td className="py-4 px-3 text-center" colSpan="5">No sample users found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="text-sm text-gray-500">Preview an audience before sending.</div>
        )}
      </section>

      <section className="bg-white rounded shadow p-4">
        <h3 className="text-lg font-bold mb-4">Push Composer</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <label className="block">
            <span className="block text-xs font-medium text-gray-600 mb-1">Campaign name</span>
            <input className="w-full border px-3 py-2 rounded" value={campaignForm.name} onChange={(event) => handleFormChange('name', event.target.value)} maxLength="120" />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-600 mb-1">Action</span>
            <select className="w-full border px-3 py-2 rounded" value={campaignForm.action} onChange={(event) => handleFormChange('action', event.target.value)}>
              {campaignActions.map((action) => <option key={action.value} value={action.value}>{action.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-600 mb-1">Push title ({campaignForm.title.length}/80)</span>
            <input className="w-full border px-3 py-2 rounded" value={campaignForm.title} onChange={(event) => handleFormChange('title', event.target.value)} maxLength="80" />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-600 mb-1">Body ({campaignForm.body.length}/180)</span>
            <input className="w-full border px-3 py-2 rounded" value={campaignForm.body} onChange={(event) => handleFormChange('body', event.target.value)} maxLength="180" />
          </label>
          {campaignForm.action === 'custom' && (
            <label className="block lg:col-span-2">
              <span className="block text-xs font-medium text-gray-600 mb-1">Deep link</span>
              <input className="w-full border px-3 py-2 rounded" value={campaignForm.deepLink} onChange={(event) => handleFormChange('deepLink', event.target.value)} placeholder="gottea://..." />
            </label>
          )}
        </div>
        <div className="flex flex-col md:flex-row md:items-center gap-3 mt-4">
          <PermissionGate permission="crm.campaigns.create"><button type="button" onClick={createDraft} className="bg-teal-600 text-white px-4 py-2 rounded flex items-center justify-center gap-2 disabled:opacity-60" disabled={submitting}>
            {submitting && <ArrowPathIcon className="size-5 animate-spin" />}
            Create Draft
          </button></PermissionGate>
          <div className="relative flex-1 min-w-[260px]">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <MagnifyingGlassIcon className="size-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  className="w-full border pl-10 pr-3 py-2 rounded"
                  value={testSearchQuery}
                  onChange={(event) => {
                    setTestSearchQuery(event.target.value);
                    setSelectedTestUser(null);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      searchTestUsers();
                    }
                  }}
                  placeholder="Search test user by phone"
                />
              </div>
              <button type="button" onClick={searchTestUsers} className="border px-4 py-2 rounded hover:bg-gray-50 disabled:opacity-60" disabled={testSearchLoading}>
                {testSearchLoading ? 'Searching...' : 'Find'}
              </button>
            </div>
            {selectedTestUser && (
              <div className="mt-2 flex items-center justify-between gap-3 rounded border border-teal-200 bg-teal-50 px-3 py-2 text-sm">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{selectedTestUser.fullName || selectedTestUser.displayName || selectedTestUser.phoneNumber || selectedTestUser.id}</div>
                  <div className="text-xs text-gray-500 truncate">{selectedTestUser.phoneNumber || selectedTestUser.email || selectedTestUser.id}</div>
                </div>
                <button type="button" className="text-gray-500 hover:text-gray-800" onClick={() => setSelectedTestUser(null)} aria-label="Clear selected test user">
                  <XMarkIcon className="size-5" />
                </button>
              </div>
            )}
            {testSearchResults.length > 0 && (
              <div className="absolute z-20 mt-2 w-full bg-white border rounded shadow-lg max-h-64 overflow-y-auto">
                {testSearchResults.map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-teal-50 border-b last:border-b-0"
                    onMouseDown={(event) => {
                      event.preventDefault();
                      chooseTestUser(user);
                    }}
                    onClick={() => chooseTestUser(user)}
                  >
                    <div className="font-semibold">{user.fullName || user.displayName || user.name || user.id}</div>
                    <div className="text-xs text-gray-500">{user.phoneNumber || '-'} {user.email ? `- ${user.email}` : ''}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <PermissionGate permission="crm.campaigns.test_send"><button type="button" onClick={handleTestSend} className="border px-4 py-2 rounded hover:bg-gray-50 disabled:opacity-60" disabled={submitting || !activeCampaignId || !selectedTestUser}>Test Send</button></PermissionGate>
          <PermissionGate permission="crm.campaigns.send"><button type="button" onClick={handleSendCampaign} className="border border-red-300 text-red-700 px-4 py-2 rounded hover:bg-red-50 disabled:opacity-60" disabled={submitting || !canSend}>Send Campaign</button></PermissionGate>
        </div>
      </section>

      <div className="grid grid-cols-1 xl:grid-cols-[360px_1fr] gap-5">
        <section className="bg-white rounded shadow p-4">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h3 className="text-lg font-bold">Campaign History</h3>
            <button type="button" onClick={loadCampaigns} className="border px-3 py-2 rounded hover:bg-gray-50" disabled={loadingCampaigns}>
              Refresh
            </button>
          </div>
          <div className="space-y-2 max-h-[520px] overflow-y-auto">
            {campaigns.map((campaign) => (
              <button
                type="button"
                key={campaign.id}
                className={`w-full text-left border rounded p-3 hover:border-teal-500 ${selected?.id === campaign.id ? 'border-teal-500 bg-teal-50' : ''}`}
                onClick={() => loadCampaignDetail(campaign.id)}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-semibold truncate">{campaign.name}</span>
                  <span className="text-xs uppercase text-gray-500">{campaign.status}</span>
                </div>
                <div className="text-xs text-gray-500 mt-1">{formatDate(campaign.createdAt)}</div>
              </button>
            ))}
            {campaigns.length === 0 && <div className="text-sm text-gray-500">No campaigns yet.</div>}
          </div>
        </section>

        <section className="bg-white rounded shadow p-4">
          {selected ? (
            <div className="space-y-5">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                <div>
                  <h3 className="text-lg font-bold">{selected.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">{selected.message?.title} - {selected.message?.body}</p>
                  <p className="text-xs text-gray-500 mt-1">Status: {selected.status} - Action: {selected.action}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {selected.status === 'draft' && (
                    <PermissionGate permission="crm.campaigns.delete"><button type="button" onClick={handleDeleteDraft} className="inline-flex items-center gap-2 border border-red-300 text-red-600 px-4 py-2 rounded hover:bg-red-50 disabled:opacity-60" disabled={submitting}>
                      <TrashIcon className="h-5 w-5" />
                      Delete Draft
                    </button></PermissionGate>
                  )}
                  <PermissionGate permission="crm.campaigns.refresh_report"><button type="button" onClick={handleRefreshReport} className="border px-4 py-2 rounded hover:bg-gray-50 disabled:opacity-60" disabled={submitting || selected.status !== 'sent'}>
                    Refresh Report
                  </button></PermissionGate>
                </div>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="border rounded p-3"><div className="text-xs text-gray-500">Target</div><div className="text-xl font-bold">{formatNumber(stats.targetCount)}</div></div>
                <div className="border rounded p-3"><div className="text-xs text-gray-500">Sent</div><div className="text-xl font-bold text-teal-600">{formatNumber(stats.sentUsers)}</div></div>
                <div className="border rounded p-3"><div className="text-xs text-gray-500">Skipped</div><div className="text-xl font-bold">{formatNumber(stats.skippedUsers)}</div></div>
                <div className="border rounded p-3"><div className="text-xs text-gray-500">Return Rate</div><div className="text-xl font-bold">{report.returnRate !== undefined ? percent(report.returnRate) : '-'}</div></div>
                <div className="border rounded p-3"><div className="text-xs text-gray-500">Token Failures</div><div className="text-xl font-bold">{formatNumber(stats.tokenFailures)}</div></div>
                <div className="border rounded p-3"><div className="text-xs text-gray-500">Stale Removed</div><div className="text-xl font-bold">{formatNumber(stats.staleTokensRemoved)}</div></div>
                <div className="border rounded p-3"><div className="text-xs text-gray-500">Returned Users</div><div className="text-xl font-bold">{formatNumber(report.returnedUsers)}</div></div>
                <div className="border rounded p-3"><div className="text-xs text-gray-500">Window</div><div className="text-xl font-bold">{report.windowDays ? `${report.windowDays}d` : '7d'}</div></div>
              </div>

              <div>
                <h4 className="font-bold mb-3">Recipients</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead>
                      <tr>
                        <th className="py-2 px-3 border-b">User</th>
                        <th className="py-2 px-3 border-b">Status</th>
                        <th className="py-2 px-3 border-b">Baseline Orders</th>
                        <th className="py-2 px-3 border-b">Current Orders</th>
                        <th className="py-2 px-3 border-b">Returned</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(selectedCampaign.recipients || []).map((recipient) => (
                        <tr key={recipient.id}>
                          <td className="py-2 px-3 border-b">{recipient.userId}</td>
                          <td className="py-2 px-3 border-b">{recipient.status}{recipient.skipReason ? `: ${recipient.skipReason}` : ''}</td>
                          <td className="py-2 px-3 border-b">{formatNumber(recipient.baseline?.orderCount)}</td>
                          <td className="py-2 px-3 border-b">{recipient.current ? formatNumber(recipient.current.orderCount) : '-'}</td>
                          <td className="py-2 px-3 border-b">{recipient.returned === true ? 'Yes' : recipient.returned === false ? 'No' : '-'}</td>
                        </tr>
                      ))}
                      {(!selectedCampaign.recipients || selectedCampaign.recipients.length === 0) && (
                        <tr><td className="py-4 px-3 text-center" colSpan="5">No recipient records loaded.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-500">Select a campaign to view details.</div>
          )}
        </section>
      </div>
    </div>
  );
}

function UserDetailDrawer({ user, onClose, onAwardComplete }) {
  const { isSuperadmin } = useAuth();
  const [activeDetailTab, setActiveDetailTab] = useState('overview');
  const [awardModalOpen, setAwardModalOpen] = useState(false);
  const [tokenAwardModalOpen, setTokenAwardModalOpen] = useState(false);
  const [rewardAwardModalOpen, setRewardAwardModalOpen] = useState(false);
  const [activity, setActivity] = useState(null);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activitySyncing, setActivitySyncing] = useState(false);
  const [activityError, setActivityError] = useState('');
  const [activitySearch, setActivitySearch] = useState({ orders: '', ledger: '' });
  const [activityPages, setActivityPages] = useState({ orders: 1, ledger: 1 });

  useEffect(() => {
    if (!user?.id) {
      setActiveDetailTab('overview');
      setActivity(null);
      setActivityError('');
      setActivitySearch({ orders: '', ledger: '' });
      setActivityPages({ orders: 1, ledger: 1 });
      setActivitySyncing(false);
      return undefined;
    }

    let active = true;
    setActiveDetailTab('overview');
    setActivity(null);
    setActivityError('');
    setActivitySearch({ orders: '', ledger: '' });
    setActivityPages({ orders: 1, ledger: 1 });
    setActivityLoading(true);
    setActivitySyncing(false);

    const loadRemainingActivity = async (section, initialCursor) => {
      let cursor = initialCursor;
      while (active && cursor) {
        const result = await fetchAppUserActivity(user.id, {
          [`${section}Cursor`]: cursor,
          [`${section}Limit`]: activityFetchBatchSize,
        });
        const nextSection = result?.[section] || { items: [], nextCursor: null };
        if (!active) return;
        setActivity((previous) => ({
          ...previous,
          [section]: {
            items: [...(previous?.[section]?.items || []), ...(nextSection.items || [])],
            nextCursor: nextSection.nextCursor || null,
          },
        }));
        cursor = nextSection.nextCursor;
      }
    };

    const loadActivity = async () => {
      try {
        const initialResult = await fetchAppUserActivity(user.id, {
          ledgerLimit: activityFetchBatchSize,
          ordersLimit: activityFetchBatchSize,
        });
        if (!active) return;
        setActivity(initialResult);
        setActivityLoading(false);

        const ledgerCursor = initialResult?.ledger?.nextCursor;
        const ordersCursor = initialResult?.orders?.nextCursor;
        if (!ledgerCursor && !ordersCursor) return;

        setActivitySyncing(true);
        await Promise.all([
          loadRemainingActivity('ledger', ledgerCursor),
          loadRemainingActivity('orders', ordersCursor),
        ]);
      } catch (error) {
        if (active) setActivityError(error.message || 'Failed to load user activity.');
      } finally {
        if (active) {
          setActivityLoading(false);
          setActivitySyncing(false);
        }
      }
    };

    loadActivity();

    return () => { active = false; };
  }, [user?.id]);

  const ledgerItems = filterActivityItems(activity?.ledger?.items || [], activitySearch.ledger, (entry) => [
    formatDate(entry.createdAt), entry.type, entry.reason, entry.orderId, entry.status, entry.amount,
  ]);
  const orderItems = filterActivityItems(activity?.orders?.items || [], activitySearch.orders, (order) => [
    formatDate(order.date), order.invoiceNumber, order.orderId, order.id, order.branchName, order.channel,
    ...(order.items || []).flatMap((item) => [item.name, item.quantity]),
  ]);
  const ledgerPageCount = Math.max(1, Math.ceil(ledgerItems.length / activityPageSize));
  const ordersPageCount = Math.max(1, Math.ceil(orderItems.length / activityPageSize));
  const visibleLedgerItems = ledgerItems.slice((activityPages.ledger - 1) * activityPageSize, activityPages.ledger * activityPageSize);
  const visibleOrderItems = orderItems.slice((activityPages.orders - 1) * activityPageSize, activityPages.orders * activityPageSize);

  if (!user) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-40 p-4">
      {awardModalOpen && (
        <ManualOrderAwardModal
          user={user}
          onClose={() => setAwardModalOpen(false)}
          onAwardComplete={onAwardComplete}
        />
      )}
      {tokenAwardModalOpen && (
        <ManualTokenAwardModal
          user={user}
          onClose={() => setTokenAwardModalOpen(false)}
          onAwardComplete={onAwardComplete}
        />
      )}
      {rewardAwardModalOpen && (
        <ManualRewardAwardModal
          user={user}
          onClose={() => setRewardAwardModalOpen(false)}
          onAwardComplete={onAwardComplete}
        />
      )}
      <div role="dialog" aria-modal="true" aria-labelledby="user-detail-title" className="flex h-[calc(100vh-2rem)] w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-4 py-4 sm:px-6">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 id="user-detail-title" className="break-words text-xl font-bold">{user.fullName || user.displayName || user.phoneNumber || user.id}</h3>
              <AppUserBadge user={user} />
              <GuestBadge user={user} />
            </div>
            <p className="text-sm text-gray-500">{user.id}</p>
          </div>
          <button type="button" onClick={onClose} className="ml-4 shrink-0 text-gray-500 hover:text-gray-800" aria-label="Close user detail">
            <XMarkIcon className="size-6" />
          </button>
        </div>

        <div className="overflow-x-auto border-b bg-gray-50 px-4 py-3 sm:px-6">
          <div role="tablist" aria-label="User details" className="inline-flex min-w-max rounded-xl bg-gray-200/80 p-1 shadow-inner">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'orders', label: 'Orders' },
              { id: 'ledger', label: 'Token Ledger' },
            ].map((tab) => (
              <button
                key={tab.id}
                id={`user-detail-tab-${tab.id}`}
                type="button"
                role="tab"
                aria-selected={activeDetailTab === tab.id}
                aria-controls="user-detail-panel"
                onClick={() => setActiveDetailTab(tab.id)}
                className={`rounded-lg px-4 py-2 text-sm font-semibold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 ${activeDetailTab === tab.id ? 'bg-white text-teal-700 shadow-sm' : 'text-gray-600 hover:bg-white/60 hover:text-gray-900'}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div
          id="user-detail-panel"
          role="tabpanel"
          aria-labelledby={`user-detail-tab-${activeDetailTab}`}
          className={`min-h-0 flex-1 space-y-6 overflow-y-auto px-4 pb-4 pt-3 sm:px-6 sm:pb-6 ${activeDetailTab === 'ledger' ? 'sm:pt-6' : 'sm:pt-3'}`}
        >
          {activeDetailTab === 'overview' && <>
            <section>
            <h4 className="font-bold mb-3">Profile</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DetailRow label="Phone" value={user.phoneNumber} />
              <DetailRow label="Email" value={user.email} />
              <DetailRow label="First Name" value={user.firstName} />
              <DetailRow label="Last Name" value={user.lastName} />
              <DetailRow label="Birthdate" value={formatBirthdate(user.dateOfBirth)} />
              <DetailRow label="Current Age" value={calculateAge(user.dateOfBirth)} />
              <DetailRow label="Created" value={formatDate(user.createdAt)} />
              <DetailRow label="Updated" value={formatDate(user.updatedAt)} />
            </div>
          </section>

          </>}

          {activeDetailTab === 'ledger' && <section>
            <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h4 className="font-bold">Token Ledger</h4>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <ActivitySearchInput
                  value={activitySearch.ledger}
                  onChange={(value) => {
                    setActivitySearch((previous) => ({ ...previous, ledger: value }));
                    setActivityPages((previous) => ({ ...previous, ledger: 1 }));
                  }}
                  placeholder="Search ledger"
                />
                {activitySyncing ? <span className="text-xs text-gray-500">Loading full history…</span> : activity?.ledger?.items?.length > 0 && <span className="text-xs text-gray-500">Newest first</span>}
              </div>
            </div>
            {activityLoading && <ActivityLoading />}
            {!activityLoading && activityError && <ActivityError message={activityError} />}
            {!activityLoading && !activityError && activity && activity.ledger.items.length === 0 && (
              <ActivityEmpty message="No token ledger entries." />
            )}
            {!activityLoading && !activityError && activity?.ledger?.items?.length > 0 && ledgerItems.length === 0 && (
              <ActivityEmpty message="No matching ledger entries in the loaded results." />
            )}
            {!activityLoading && !activityError && ledgerItems.length > 0 && (
              <>
                <div className="overflow-x-auto border rounded">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs text-gray-500">
                      <tr>
                        <th className="px-3 py-2">Date</th>
                        <th className="px-3 py-2">Type / Reason</th>
                        <th className="px-3 py-2 text-right">Tokens</th>
                        <th className="px-3 py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleLedgerItems.map((entry) => (
                        <tr key={entry.id} className="border-t align-top">
                          <td className="px-3 py-2 whitespace-nowrap">{formatDate(entry.createdAt)}</td>
                          <td className="px-3 py-2">
                            <div className="font-medium">{entry.type || '-'}</div>
                            <div className="text-xs text-gray-500 break-words">{entry.reason || entry.orderId || '-'}</div>
                          </td>
                          <td className={`px-3 py-2 text-right font-semibold whitespace-nowrap ${Number(entry.amount) < 0 ? 'text-red-600' : 'text-teal-600'}`}>
                            {Number(entry.amount) > 0 ? '+' : ''}{formatNumber(entry.amount)}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">{entry.status || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            {!activityLoading && !activityError && ledgerItems.length > 0 && (
              <ActivityPagination
                currentPage={activityPages.ledger}
                pageCount={ledgerPageCount}
                onPageChange={(page) => setActivityPages((previous) => ({ ...previous, ledger: page }))}
              />
            )}
          </section>}

          {activeDetailTab === 'orders' && <section>
            <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h4 className="font-bold">Order History</h4>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <ActivitySearchInput
                  value={activitySearch.orders}
                  onChange={(value) => {
                    setActivitySearch((previous) => ({ ...previous, orders: value }));
                    setActivityPages((previous) => ({ ...previous, orders: 1 }));
                  }}
                  placeholder="Search orders"
                />
                {activitySyncing ? <span className="text-xs text-gray-500">Loading full history…</span> : activity?.orders?.items?.length > 0 && <span className="text-xs text-gray-500">Newest first</span>}
              </div>
            </div>
            {activityLoading && <ActivityLoading />}
            {!activityLoading && activityError && <ActivityError message={activityError} />}
            {!activityLoading && !activityError && activity && activity.orders.items.length === 0 && (
              <ActivityEmpty message="No orders found." />
            )}
            {!activityLoading && !activityError && activity?.orders?.items?.length > 0 && orderItems.length === 0 && (
              <ActivityEmpty message="No matching orders in the loaded results." />
            )}
            {!activityLoading && !activityError && orderItems.length > 0 && (
              <>
                <div className="overflow-x-auto border rounded">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-left text-xs text-gray-500">
                      <tr>
                        <th className="px-3 py-2">Date / Order</th>
                        <th className="px-3 py-2">Items</th>
                        <th className="px-3 py-2 text-right">Amount</th>
                        <th className="px-3 py-2 text-right">Tokens</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleOrderItems.map((order) => (
                        <tr key={order.id} className="border-t align-top">
                          <td className="px-3 py-2">
                            <div className="whitespace-nowrap">{formatDate(order.date)}</div>
                            <div className="text-xs text-gray-500 break-all">{order.invoiceNumber || order.orderId || order.id}</div>
                            <div className="text-xs text-gray-500">{order.branchName || order.channel || '-'}</div>
                          </td>
                          <td className="px-3 py-2 min-w-[180px]">
                            {(order.items || []).map((item, index) => (
                              <div key={`${item.name || 'item'}-${index}`}>{item.name || '-'} × {formatNumber(item.quantity)}</div>
                            ))}
                            {(!order.items || order.items.length === 0) && '-'}
                          </td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">{formatCurrency(order.totalAmount)}</td>
                          <td className="px-3 py-2 text-right font-semibold text-teal-600">{formatNumber(order.tokensEarned)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            {!activityLoading && !activityError && orderItems.length > 0 && (
              <ActivityPagination
                currentPage={activityPages.orders}
                pageCount={ordersPageCount}
                onPageChange={(page) => setActivityPages((previous) => ({ ...previous, orders: page }))}
              />
            )}
          </section>}

          {activeDetailTab === 'overview' && <>
          <section>
            <div className="flex items-center justify-between gap-3 mb-3">
              <h4 className="font-bold">Loyalty</h4>
              <div className="flex flex-wrap gap-2">
                <PermissionGate permission="crm.manual_awards.award_tokens"><button
                    type="button"
                    onClick={() => setTokenAwardModalOpen(true)}
                    className="border border-teal-500 text-teal-700 px-3 py-2 rounded flex items-center gap-2 hover:bg-teal-50"
                  >
                    <SparklesIcon className="size-5" />
                    Award Tokens
                  </button>
                </PermissionGate>
                {isSuperadmin && <>
                  <button
                    type="button"
                    onClick={() => setRewardAwardModalOpen(true)}
                    className="border border-teal-500 text-teal-700 px-3 py-2 rounded flex items-center gap-2 hover:bg-teal-50"
                  >
                    <SparklesIcon className="size-5" />
                    Grant Reward
                  </button>
                </>}
                <PermissionGate permission="crm.manual_awards.award_orders"><button
                  type="button"
                  onClick={() => setAwardModalOpen(true)}
                  className="border border-teal-500 text-teal-700 px-3 py-2 rounded flex items-center gap-2 hover:bg-teal-50"
                >
                  <PlusCircleIcon className="size-5" />
                  Award Order
                </button></PermissionGate>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="border rounded p-3">
                <div className="text-xs text-gray-500">Balance</div>
                <div className="text-xl font-bold text-teal-600">{formatNumber(user.tokenBalance)}</div>
              </div>
              <div className="border rounded p-3">
                <div className="text-xs text-gray-500">Orders</div>
                <div className="text-xl font-bold">{formatNumber(user.orderCount)}</div>
              </div>
              <div className="border rounded p-3 min-w-0 overflow-hidden">
                <div className="text-xs text-gray-500">Net Spend</div>
                <div className="text-lg font-bold whitespace-nowrap">{formatCurrency(user.lifetimeNetSpend)}</div>
              </div>
              <div className="border rounded p-3">
                <div className="text-xs text-gray-500">Streak</div>
                <div className="text-lg font-bold whitespace-nowrap">
                  {formatNumber(user.currentStreak)} <span className="text-sm font-normal text-gray-500">current</span>
                  <span className="text-gray-400"> / </span>
                  {formatNumber(user.bestStreak)} <span className="text-sm font-normal text-gray-500">best</span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <DetailRow label="Lifetime Tokens Earned" value={formatNumber(user.lifetimeTokensEarned)} />
              <DetailRow label="Lifetime Tokens Spent" value={formatNumber(user.lifetimeTokensSpent)} />
              <DetailRow label="Lifetime Net Spend" value={formatCurrency(user.lifetimeNetSpend)} />
              <DetailRow label="Lifetime Reversed" value={formatNumber(user.lifetimeTokensReversed)} />
              <DetailRow label="Streak Visit Day" value={formatDate(user.lastVisitDate)} />
            </div>
          </section>

          <section>
            <h4 className="font-bold mb-3">Referral</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DetailRow label="Referral Code" value={user.referralCode} />
              <DetailRow label="Referred By" value={user.referredByCode} />
              <DetailRow label="Signup Status" value={user.referralSignupRewardStatus} />
              <DetailRow label="First Order Status" value={user.referralFirstOrderRewardStatus} />
            </div>
          </section>

          <section>
            <h4 className="font-bold mb-3">Top Sips</h4>
            {Array.isArray(user.topSips) && user.topSips.length > 0 ? (
              <div className="space-y-2">
                {user.topSips.slice(0, 5).map((sip, index) => (
                  <div key={`${sip.sku || sip.name}-${index}`} className="flex items-center justify-between border rounded px-3 py-2">
                    <span className="font-medium">{sip.name || sip.sku || '-'}</span>
                    <span className="text-sm text-gray-500">{formatNumber(sip.count)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-500">No top sips recorded.</div>
            )}
          </section>
          </>}
        </div>
      </div>
    </div>
  );
}

function ActivityLoading() {
  return <div className="border rounded px-3 py-4 text-sm text-gray-500 flex items-center gap-2"><ArrowPathIcon className="size-4 animate-spin" /> Loading activity…</div>;
}

function ActivityError({ message }) {
  return <div className="border border-red-200 bg-red-50 text-red-700 rounded px-3 py-2 text-sm">{message}</div>;
}

function ActivityEmpty({ message }) {
  return <div className="border rounded px-3 py-4 text-sm text-gray-500">{message}</div>;
}

function filterActivityItems(items, query, fieldsForItem) {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return items;

  return items.filter((item) => fieldsForItem(item)
    .filter((value) => value !== null && value !== undefined)
    .join(' ')
    .toLowerCase()
    .includes(normalizedQuery));
}

function ActivitySearchInput({ value, onChange, placeholder }) {
  return (
    <div className="relative min-w-0 sm:w-60">
      <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
      <input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100"
      />
    </div>
  );
}

function getPaginationItems(currentPage, pageCount) {
  if (pageCount <= 5) return Array.from({ length: pageCount }, (_, index) => index + 1);

  const pages = [1];
  if (currentPage > 3) pages.push('start-ellipsis');
  for (let page = Math.max(2, currentPage - 1); page <= Math.min(pageCount - 1, currentPage + 1); page += 1) {
    pages.push(page);
  }
  if (currentPage < pageCount - 2) pages.push('end-ellipsis');
  pages.push(pageCount);
  return pages;
}

function ActivityPagination({ currentPage, pageCount, onPageChange }) {
  if (pageCount <= 1) return null;

  return (
    <nav className="mt-4 flex flex-wrap items-center justify-center gap-1" aria-label="Activity pagination">
      <button
        type="button"
        className="rounded-md border px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
      >
        Previous
      </button>
      {getPaginationItems(currentPage, pageCount).map((item) => (
        typeof item === 'number' ? (
          <button
            key={item}
            type="button"
            aria-label={`Page ${item}`}
            aria-current={item === currentPage ? 'page' : undefined}
            onClick={() => onPageChange(item)}
            className={`min-w-9 rounded-md px-3 py-1.5 text-sm font-semibold ${item === currentPage ? 'bg-teal-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
          >
            {item}
          </button>
        ) : <span key={item} className="px-1 text-sm text-gray-500" aria-hidden="true">…</span>
      ))}
      <button
        type="button"
        className="rounded-md border px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === pageCount}
      >
        Next
      </button>
    </nav>
  );
}

function AppUsersCRM() {
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState(emptyFilters);
  const [activeFilters, setActiveFilters] = useState(emptyFilters);
  const [users, setUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortField, setSortField] = useState('updatedAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [loading, setLoading] = useState(false);
  const [chartsLoading, setChartsLoading] = useState(false);
  const [charts, setCharts] = useState(null);
  const [signupTrendDateRange, setSignupTrendDateRange] = useState({ start: '', end: '' });
  const [exporting, setExporting] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeTab, setActiveTab] = useState('users');
  const [filtersOpen, setFiltersOpen] = useState(false);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const activeFilterCount = useMemo(() => countActiveAdvancedFilters(filters), [filters]);

  const requestPayload = useMemo(() => ({
    query,
    filters: buildFilters(activeFilters),
    sort: { field: sortField, direction: sortDirection },
    page,
    pageSize,
  }), [query, activeFilters, sortField, sortDirection, page, pageSize]);

  const campaignAudience = useMemo(() => ({
    query,
    filters: buildFilters(filters),
    sort: { field: sortField, direction: sortDirection },
  }), [query, filters, sortField, sortDirection]);

  const chartsPayload = useMemo(() => ({
    query,
    filters: buildFilters(activeFilters),
    signupTrendDateRange: {
      start: signupTrendDateRange.start || undefined,
      end: signupTrendDateRange.end ? `${signupTrendDateRange.end}T23:59:59.999` : undefined,
    },
  }), [query, activeFilters, signupTrendDateRange]);

  const loadUsers = async (payload = requestPayload) => {
    setLoading(true);
    try {
      const result = await searchAppUsers(payload);
      const nextUsers = result.users || [];
      setUsers(nextUsers);
      setSelectedUser((previous) => {
        if (!previous) return previous;
        return nextUsers.find((user) => user.id === previous.id) || previous;
      });
      setTotalCount(result.totalCount || 0);
      if (result.page && result.page !== page) setPage(result.page);
    } catch (error) {
      setAlertMessage(error.message || 'Failed to search app users.');
    } finally {
      setLoading(false);
    }
  };

  const loadCharts = async (payload = chartsPayload) => {
    setChartsLoading(true);
    try {
      const result = await fetchAppUserCharts(payload);
      setCharts(result);
    } catch (error) {
      setAlertMessage(error.message || 'Failed to load app user charts.');
    } finally {
      setChartsLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    loadCharts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilters, sortField, sortDirection, page, pageSize, signupTrendDateRange]);

  const applyFilters = () => {
    setPage(1);
    setActiveFilters(filters);
    loadUsers({
      query,
      filters: buildFilters(filters),
      sort: { field: sortField, direction: sortDirection },
      page: 1,
      pageSize,
    });
    loadCharts({
      query,
      filters: buildFilters(filters),
      signupTrendDateRange: {
        start: signupTrendDateRange.start || undefined,
        end: signupTrendDateRange.end ? `${signupTrendDateRange.end}T23:59:59.999` : undefined,
      },
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    applyFilters();
  };

  const handleFilterChange = (field, value) => {
    setFilters((previous) => ({ ...previous, [field]: value }));
  };

  const handleApplyFilters = () => {
    setFiltersOpen(false);
    applyFilters();
  };

  const handleClearFilters = () => {
    const cleared = emptyFilters();
    setFilters(cleared);
  };

  const handleReset = () => {
    const cleared = emptyFilters();
    setQuery('');
    setFilters(cleared);
    setActiveFilters(cleared);
    setPage(1);
  };

  const handleApplyAudience = (audience) => {
    const nextFilters = filtersFromAudienceFilters(audience?.filters || {});
    const nextQuery = audience?.query || '';
    const nextSortField = audience?.sort?.field || sortField;
    const nextSortDirection = audience?.sort?.direction || sortDirection;

    setQuery(nextQuery);
    setFilters(nextFilters);
    setActiveFilters(nextFilters);
    setSortField(nextSortField);
    setSortDirection(nextSortDirection);
    setPage(1);

    loadUsers({
      query: nextQuery,
      filters: buildFilters(nextFilters),
      sort: { field: nextSortField, direction: nextSortDirection },
      page: 1,
      pageSize,
    });
    loadCharts({
      query: nextQuery,
      filters: buildFilters(nextFilters),
      signupTrendDateRange: {
        start: signupTrendDateRange.start || undefined,
        end: signupTrendDateRange.end ? `${signupTrendDateRange.end}T23:59:59.999` : undefined,
      },
    });
  };

  const handleManualAwardComplete = (result) => {
    if (result && Number.isFinite(Number(result.amount)) && result.tokenBalanceAfter !== undefined) {
      const amount = Number(result.amount) || 0;
      setAlertMessage(`${formatNumber(amount)} Got Tokens awarded successfully. New balance: ${formatNumber(result.tokenBalanceAfter)}.`);
      setSelectedUser((previous) => {
        if (!previous || !result || previous.id !== result.userId) return previous;
        return { ...previous, tokenBalance: result.tokenBalanceAfter };
      });
    } else if (result?.rewardId && result?.couponCode) {
      setAlertMessage(`${result.reward?.title || 'Reward'} granted successfully. Coupon: ${result.couponCode}.`);
    } else {
      const tokens = result?.loyaltyResult?.tokensAwarded ?? 0;
      setAlertMessage(`Order awarded successfully. ${formatNumber(tokens)} Got Tokens posted.`);
    }
    loadUsers();
    loadCharts();
  };

  const handleExportCsv = async (audienceOverride = null) => {
    setExporting(true);
    try {
      const exportAudience = audienceOverride && !audienceOverride.preventDefault ? audienceOverride : null;
      const exportPayload = {
        query: exportAudience ? exportAudience.query : query,
        filters: exportAudience ? exportAudience.filters : buildFilters(activeFilters),
        sort: exportAudience ? exportAudience.sort : { field: sortField, direction: sortDirection },
      };
      await createCrmUserExport(exportPayload);
      setAlertMessage(
        <span>
          Export queued. Track it on the{' '}
          <Link className="text-teal-700 font-semibold underline" to="/crm/downloads">
            Downloads
          </Link>
          {' '}page.
        </span>
      );
    } catch (error) {
      setAlertMessage(error.message || 'Failed to queue app user export.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="m-auto p-6 md:p-10 max-w-screen-2xl">
      {alertMessage && <Alert message={alertMessage} onClose={() => setAlertMessage(null)} />}
      <UserDetailDrawer user={selectedUser} onClose={() => setSelectedUser(null)} onAwardComplete={handleManualAwardComplete} />
      {filtersOpen && (
        <CrmFiltersModal
          filters={filters}
          onChange={handleFilterChange}
          onClose={() => setFiltersOpen(false)}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
        />
      )}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold">App Users CRM</h2>
          <p className="text-gray-500 mt-1">{formatNumber(totalCount)} users found</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={() => handleExportCsv()}
            className="border px-4 py-2 rounded flex items-center gap-2 hover:bg-gray-50 w-fit disabled:opacity-50"
            disabled={loading || chartsLoading || exporting}
          >
            {exporting ? (
              <ArrowPathIcon className="size-5 animate-spin" />
            ) : (
              <ArrowDownTrayIcon className="size-5" />
            )}
            {exporting ? 'Queueing...' : 'Export CSV'}
          </button>
          <button
            type="button"
            onClick={() => {
              loadUsers();
              loadCharts();
            }}
            className="border px-4 py-2 rounded flex items-center gap-2 hover:bg-gray-50 w-fit disabled:opacity-50"
            disabled={loading || chartsLoading || exporting}
          >
            <ArrowPathIcon className={`size-5 ${loading || chartsLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded shadow p-4 mb-5">
        <div className="grid grid-cols-1 2xl:grid-cols-[minmax(260px,1fr)_repeat(3,180px)_auto] gap-3">
          <div className="relative">
            <MagnifyingGlassIcon className="size-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="search"
              className="w-full border pl-10 pr-3 py-2 rounded"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Name, phone, email, referral code"
            />
          </div>
          <select
            className="border px-3 py-2 rounded"
            value={sortField}
            onChange={(event) => {
              setSortField(event.target.value);
              setPage(1);
            }}
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <select
            className="border px-3 py-2 rounded"
            value={sortDirection}
            onChange={(event) => {
              setSortDirection(event.target.value);
              setPage(1);
            }}
          >
            <option value="desc">Descending</option>
            <option value="asc">Ascending</option>
          </select>
          <select
            className="border px-3 py-2 rounded"
            value={pageSize}
            onChange={(event) => {
              setPageSize(Number(event.target.value));
              setPage(1);
            }}
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>{size} rows</option>
            ))}
          </select>
          <div className="flex gap-2">
            <button type="button" onClick={() => setFiltersOpen(true)} className="border px-4 py-2 rounded flex items-center gap-2 hover:bg-gray-50">
              <FunnelIcon className="size-5" />
              Filters
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-5 h-5 rounded-full bg-teal-600 px-1.5 text-xs font-semibold text-white">
                  {activeFilterCount}
                </span>
              )}
            </button>
            <PermissionGate permission="crm.users.search"><button type="submit" className="bg-teal-500 text-white px-4 py-2 rounded flex items-center gap-2">
              <MagnifyingGlassIcon className="size-5" />
              Search
            </button></PermissionGate>
            <button type="button" onClick={handleReset} className="border px-4 py-2 rounded hover:bg-gray-50">
              Reset
            </button>
          </div>
        </div>
      </form>

      <div className="flex gap-2 mb-5 border-b">
        <button
          type="button"
          onClick={() => setActiveTab('users')}
          className={`px-4 py-3 font-semibold border-b-2 ${activeTab === 'users' ? 'border-teal-500 text-teal-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
        >
          Users
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('charts')}
          className={`px-4 py-3 font-semibold border-b-2 ${activeTab === 'charts' ? 'border-teal-500 text-teal-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
        >
          Charts
        </button>
        <PermissionGate permission="crm.campaigns.view"><button
          type="button"
          onClick={() => setActiveTab('campaigns')}
          className={`px-4 py-3 font-semibold border-b-2 ${activeTab === 'campaigns' ? 'border-teal-500 text-teal-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
        >
          Campaigns
        </button></PermissionGate>
      </div>

      {activeTab === 'charts' ? (
        <ChartsDashboard
          charts={charts}
          loading={chartsLoading}
          signupTrendDateRange={signupTrendDateRange}
          onSignupTrendDateChange={(field, value) => setSignupTrendDateRange((prev) => ({ ...prev, [field]: value }))}
          onResetSignupTrendDate={() => setSignupTrendDateRange({ start: '', end: '' })}
        />
      ) : activeTab === 'campaigns' ? (
        <CampaignsDashboard
          currentAudience={campaignAudience}
          onApplyAudience={handleApplyAudience}
          onExportAudience={() => handleExportCsv(campaignAudience)}
          setAlertMessage={setAlertMessage}
        />
      ) : (
        <>
          {loading ? (
            <div className="text-center py-10">
              <div className="loader border-t-4 border-teal-500 border-solid rounded-full w-12 h-12 mx-auto animate-spin"></div>
              <p className="mt-2">Loading app users...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white shadow rounded text-left">
                <thead>
                  <tr>
                    <th className="py-2 px-4 border-b">User</th>
                    <th className="py-2 px-4 border-b">Contact</th>
                    <th className="py-2 px-4 border-b">Tokens</th>
                    <th className="py-2 px-4 border-b">Net Spend</th>
                    <th className="py-2 px-4 border-b">Orders</th>
                    <th className="py-2 px-4 border-b hidden md:table-cell">Streak</th>
                    <th className="py-2 px-4 border-b hidden lg:table-cell">Referral</th>
                    <th className="py-2 px-4 border-b hidden xl:table-cell">Sign Up</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-teal-50 cursor-pointer" onClick={() => setSelectedUser(user)}>
                      <td className="py-3 px-4 border-b">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold">{user.fullName || user.displayName || user.name || '-'}</span>
                          <AppUserBadge user={user} />
                          <GuestBadge user={user} />
                        </div>
                        <div className="text-xs text-gray-500">{user.id}</div>
                      </td>
                      <td className="py-3 px-4 border-b">
                        <div>{user.phoneNumber || '-'}</div>
                        <div className="text-xs text-gray-500">{user.email || '-'}</div>
                      </td>
                      <td className="py-3 px-4 border-b font-semibold text-teal-600">{formatNumber(user.tokenBalance)}</td>
                      <td className="py-3 px-4 border-b">{formatCurrency(user.lifetimeNetSpend)}</td>
                      <td className="py-3 px-4 border-b">{formatNumber(user.orderCount)}</td>
                      <td className="py-3 px-4 border-b hidden md:table-cell">
                        {formatNumber(user.currentStreak)} / {formatNumber(user.bestStreak)}
                      </td>
                      <td className="py-3 px-4 border-b hidden lg:table-cell">
                        <div>{user.referralCode || '-'}</div>
                        <div className="text-xs text-gray-500">{user.referralSignupRewardStatus || '-'}</div>
                      </td>
                      <td className="py-3 px-4 border-b hidden xl:table-cell">{formatDate(user.createdAt)}</td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr>
                      <td colSpan="8" className="text-center py-8">No app users found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {activeTab === 'users' && (
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mt-5">
          <div className="text-sm text-gray-500">
            Page {page} of {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="border px-3 py-2 rounded flex items-center gap-1 disabled:opacity-50"
              disabled={page <= 1 || loading}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
            >
              <ChevronLeftIcon className="size-5" />
              Previous
            </button>
            <button
              type="button"
              className="border px-3 py-2 rounded flex items-center gap-1 disabled:opacity-50"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
            >
              Next
              <ChevronRightIcon className="size-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AppUsersCRM;
